import type {
  EntityBody,
  SpaceConfig,
  TransitionRecording,
} from "@cloudnest/stone-flicking-core-engine/types";
import { useCallback, useMemo, useRef } from "react";
import { useCanvasRenderer } from "./hooks/use-canvas-renderer";
import { useFlickInput } from "./hooks/use-flick-input";
import {
  canvasToBoard,
  computeCircularLayout,
} from "./utils/coordinate-transform";

interface GameCanvasProps {
  readonly entities: ReadonlyArray<EntityBody>;
  readonly selectedEntityId: string | null;
  readonly isAnimating: boolean;
  readonly animationRecording: TransitionRecording | null;
  readonly myPlayerId: string | null;
  readonly playerCount: number;
  readonly readyPlayers: ReadonlyArray<string>;
  readonly spaceConfig: SpaceConfig;
  readonly canInteract: boolean;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly canvasPadding: number;
  readonly externalFlickPreview?: {
    readonly dx: number;
    readonly dy: number;
  } | null;
  readonly onSelectEntity: (entityId: string | null) => void;
  readonly onFlick: (
    entityId: string,
    impulseX: number,
    impulseY: number,
  ) => void;
  readonly onAnimationComplete: () => void;
}

export function GameCanvas({
  entities,
  selectedEntityId,
  isAnimating,
  animationRecording,
  myPlayerId,
  playerCount,
  readyPlayers,
  spaceConfig,
  canInteract,
  canvasWidth,
  canvasHeight,
  canvasPadding,
  externalFlickPreview,
  onSelectEntity,
  onFlick,
  onAnimationComplete,
}: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const myPlayerIndex = myPlayerId
    ? Number.parseInt(myPlayerId.replace("P", ""), 10) - 1
    : 0;

  const layout = useMemo(
    () =>
      computeCircularLayout(
        canvasWidth,
        canvasHeight,
        canvasPadding,
        spaceConfig,
        myPlayerIndex,
        playerCount,
      ),
    [
      canvasWidth,
      canvasHeight,
      canvasPadding,
      spaceConfig,
      myPlayerIndex,
      playerCount,
    ],
  );

  const selectedEntity = selectedEntityId
    ? (entities.find((e) => e.id === selectedEntityId && !e.eliminated) ?? null)
    : null;

  const handleFlickResult = useCallback(
    (result: { impulseX: number; impulseY: number }) => {
      if (selectedEntityId && !isAnimating && canInteract) {
        onFlick(selectedEntityId, result.impulseX, result.impulseY);
      }
    },
    [selectedEntityId, isAnimating, canInteract, onFlick],
  );

  const { dragState, handlePointerDown, handlePointerMove, handlePointerUp } =
    useFlickInput(layout, handleFlickResult);

  const currentFlickPreview = useMemo(() => {
    if (selectedEntity && externalFlickPreview) {
      return {
        fromX: selectedEntity.x,
        fromY: selectedEntity.y,
        toX: selectedEntity.x - externalFlickPreview.dx,
        toY: selectedEntity.y - externalFlickPreview.dy,
      };
    }
    if (!dragState?.isDragging || !selectedEntity) return null;
    const dx = dragState.startBoardX - dragState.currentBoardX;
    const dy = dragState.startBoardY - dragState.currentBoardY;
    return {
      fromX: selectedEntity.x,
      fromY: selectedEntity.y,
      toX: selectedEntity.x + dx,
      toY: selectedEntity.y + dy,
    };
  }, [dragState, selectedEntity, externalFlickPreview]);

  useCanvasRenderer(
    canvasRef,
    layout,
    entities,
    selectedEntityId,
    playerCount,
    readyPlayers,
    currentFlickPreview,
    animationRecording,
    onAnimationComplete,
  );

  const handleCanvasPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isAnimating || !canInteract) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;
      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;
      const board = canvasToBoard(canvasX, canvasY, layout);

      const clickedEntity = findEntityAtBoard(
        entities,
        board.x,
        board.y,
        myPlayerId,
      );

      if (clickedEntity) {
        if (selectedEntityId === clickedEntity.id) {
          handlePointerDown(canvasX, canvasY);
        } else {
          onSelectEntity(clickedEntity.id);
        }
      } else if (selectedEntityId) {
        handlePointerDown(canvasX, canvasY);
      } else {
        onSelectEntity(null);
      }
    },
    [
      isAnimating,
      canInteract,
      canvasWidth,
      canvasHeight,
      layout,
      entities,
      myPlayerId,
      selectedEntityId,
      onSelectEntity,
      handlePointerDown,
    ],
  );

  const handleCanvasPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;
      handlePointerMove(
        (e.clientX - rect.left) * scaleX,
        (e.clientY - rect.top) * scaleY,
      );
    },
    [canvasWidth, canvasHeight, handlePointerMove],
  );

  const handleCanvasPointerUp = useCallback(() => {
    handlePointerUp();
  }, [handlePointerUp]);

  return (
    <canvas
      data-testid="game-canvas"
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      style={{
        maxWidth: "100%",
        height: "auto",
        borderRadius: "50%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        cursor: isAnimating
          ? "wait"
          : selectedEntityId
            ? "crosshair"
            : "pointer",
        touchAction: "none",
      }}
      onPointerDown={handleCanvasPointerDown}
      onPointerMove={handleCanvasPointerMove}
      onPointerUp={handleCanvasPointerUp}
      onPointerLeave={handleCanvasPointerUp}
    />
  );
}

const HIT_AREA_MULTIPLIER = 2.5;

function findEntityAtBoard(
  entities: ReadonlyArray<EntityBody>,
  boardX: number,
  boardY: number,
  myPlayerId: string | null,
): EntityBody | null {
  let closest: EntityBody | null = null;
  let closestDist = Number.POSITIVE_INFINITY;

  for (const entity of entities) {
    if (entity.eliminated || entity.owner !== myPlayerId) continue;
    const dx = entity.x - boardX;
    const dy = entity.y - boardY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const hitRadius = entity.radius * HIT_AREA_MULTIPLIER;
    if (dist <= hitRadius && dist < closestDist) {
      closest = entity;
      closestDist = dist;
    }
  }

  return closest;
}
